import {StopIcon} from '@primer/octicons-react'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Dialog, Flash, Link, type ButtonProps} from '@primer/react'
import {createContext, use, useCallback, useMemo, useState, type PropsWithChildren} from 'react'
import {announce} from '@github-ui/aria-live'
import {useAnalytics} from '@github-ui/use-analytics'
import {useDequeuePullRequestMutation} from '../../../hooks/mutations/use-dequeue-pull-request-mutation'
import type {MergeQueue as MergeQueueData, MergeQueueEntry as MergeQueueEntryData} from '../../../types'
import {useSyncMergeQueueFavicon} from '../../../hooks/use-sync-favicon'
import {useIsOverviewEnabled} from '@github-ui/pull-requests/use-feature-state'
import {useIsMergeBoxSidePanel} from '../../../contexts/MergeBoxLayoutContext'

import styles from './MergeQueueEntry.module.css'

type MergeQueueDataContextValue = {
  mergeQueue: MergeQueueData
  mergeQueueEntry: MergeQueueEntryData
  viewerCanAddAndRemoveFromMergeQueue: boolean
}

const MergeQueueDataContext = createContext<MergeQueueDataContextValue | null>(null)
const MergeQueueOpenConfirmationContext = createContext<(() => void) | null>(null)

export type Props = PropsWithChildren<
  MergeQueueDataContextValue & {
    onDequeueSuccess: () => void
    analyticsTargetPrefix?: string
  }
>

function useMergeQueueData() {
  const context = use(MergeQueueDataContext)
  if (!context) throw new Error('MergeQueueEntry compound components must be rendered within MergeQueueEntry')
  return context
}

function useOpenConfirmation() {
  const context = use(MergeQueueOpenConfirmationContext)
  if (!context) throw new Error('MergeQueueEntry.DequeueAction must be rendered within MergeQueueEntry')
  return context
}

function MergeQueueEntryRoot({
  mergeQueue,
  mergeQueueEntry,
  viewerCanAddAndRemoveFromMergeQueue,
  onDequeueSuccess,
  analyticsTargetPrefix = 'MERGEBOX',
  children,
}: Props) {
  const [isConfirmationDialogOpen, setIsConfirmationDialogOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const overviewEnabled = useIsOverviewEnabled()
  const isSidePanel = useIsMergeBoxSidePanel()
  useSyncMergeQueueFavicon({enabled: !isSidePanel && !overviewEnabled})

  const {sendAnalyticsEvent} = useAnalytics()

  const {mutate: onRemoveFromQueue, isPending} = useDequeuePullRequestMutation({
    onError: (e: Error) => {
      setIsConfirmationDialogOpen(false)
      setErrorMessage(e.message)
      setTimeout(() => announce('Failed to remove pull request from the merge queue'), 1000)
    },
  })

  const handleDequeue = () => {
    if (isPending) return
    setErrorMessage(null)
    sendAnalyticsEvent(
      'merge_queue_section.dequeue_pull_request',
      `${analyticsTargetPrefix}_MERGE_QUEUE_SECTION_REMOVE_FROM_QUEUE_BUTTON`,
    )

    onRemoveFromQueue(undefined, {
      onSuccess: () => {
        setIsConfirmationDialogOpen(false)
        setTimeout(() => announce('The pull request was successfully removed from the queue.'), 1000)
        onDequeueSuccess()
      },
    })
  }

  const handleDismiss = () => {
    if (!isPending) setIsConfirmationDialogOpen(false)
  }

  const handleOpenConfirmation = useCallback(() => {
    setErrorMessage(null)
    setIsConfirmationDialogOpen(true)
  }, [])

  const data = useMemo(
    () => ({mergeQueue, mergeQueueEntry, viewerCanAddAndRemoveFromMergeQueue}),
    [mergeQueue, mergeQueueEntry, viewerCanAddAndRemoveFromMergeQueue],
  )

  return (
    <MergeQueueDataContext value={data}>
      <MergeQueueOpenConfirmationContext value={handleOpenConfirmation}>
        {isConfirmationDialogOpen && (
          <Dialog
            title="Remove from the queue?"
            role="alertdialog"
            onClose={handleDismiss}
            footerButtons={[
              {content: 'Cancel', buttonType: 'default', onClick: handleDismiss, disabled: isPending, autoFocus: true},
              {
                content: 'Remove from the queue',
                buttonType: 'danger',
                onClick: handleDequeue,
                loading: isPending,
                loadingAnnouncement: 'Removing from the queue',
              },
            ]}
          >
            Removing this pull request from the queue could impact other pull requests in the queue. Are you sure?
          </Dialog>
        )}
        {errorMessage && (
          <Flash className="tmp-mx-3 my-2" variant="danger">
            <StopIcon className="mr-2" />
            {errorMessage}
          </Flash>
        )}
        {children}
      </MergeQueueOpenConfirmationContext>
    </MergeQueueDataContext>
  )
}

function MergeQueueEntryTitle() {
  return <>Queued to merge...</>
}

function MergeQueueEntryDescription() {
  const {mergeQueue, mergeQueueEntry} = useMergeQueueData()
  const position = mergeQueueEntry?.position
  const resourcePath = mergeQueue?.url
  const entryIsLocked = mergeQueueEntry?.isLocked

  if (!position || !resourcePath) return <></>
  const pullsBeforeInQueue = position - 1

  let text: string = ''
  if (entryIsLocked) {
    text = `This pull request is locked for deployment by the`
  } else if (pullsBeforeInQueue === 0) {
    text = 'This pull request is next up in the'
  } else if (pullsBeforeInQueue === 1) {
    text = `There is ${pullsBeforeInQueue} pull request ahead of this one in the`
  } else {
    text = `There are ${pullsBeforeInQueue} pull requests ahead of this one in the`
  }

  return (
    <>
      <span className={styles.MergeQueueSectionText}>{text}</span>{' '}
      <Link inline href={resourcePath}>
        merge queue
      </Link>
      .
    </>
  )
}

function MergeQueueEntryDequeueAction({buttonProps}: {buttonProps?: Omit<ButtonProps, 'onClick'>}) {
  const {mergeQueueEntry, viewerCanAddAndRemoveFromMergeQueue} = useMergeQueueData()
  const openConfirmation = useOpenConfirmation()

  if (!viewerCanAddAndRemoveFromMergeQueue || mergeQueueEntry?.isLocked) return null

  return (
    <Button {...buttonProps} onClick={openConfirmation}>
      Remove from queue
    </Button>
  )
}

export const MergeQueueEntry = Object.assign(MergeQueueEntryRoot, {
  Title: MergeQueueEntryTitle,
  Description: MergeQueueEntryDescription,
  DequeueAction: MergeQueueEntryDequeueAction,
})
